package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.example.project.settings.LogoSettings
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

private const val MOBILE_NAV_HIDE_DELAY_MS = 3_000L

@Composable
fun MobileFocusCloseEffect(onClose: () -> Unit) {
    DisposableEffect(onClose) {
        val handler: (Event) -> Unit = { event ->
            val type = event.asDynamic().data?.type as? String
            if (type == "lumatica-focus-close") onClose()
        }
        window.addEventListener("message", handler)
        onDispose { window.removeEventListener("message", handler) }
    }
}

@Composable
fun AutoHidingMobileDashboardNavigation(
    heightPx: Int,
    content: @Composable () -> Unit
) {
    var visible by remember { mutableStateOf(true) }
    var activityRevision by remember { mutableStateOf(0) }

    LaunchedEffect(activityRevision) {
        visible = true
        delay(MOBILE_NAV_HIDE_DELAY_MS)
        visible = false
    }

    Div({
        onClick { activityRevision++ }
        style {
            height(heightPx.px)
            minHeight(heightPx.px)
            position(Position.Relative)
            property("z-index", "50001")
            opacity(if (visible) 1 else 0)
            property("transition", "opacity 180ms ease")
            property("touch-action", "manipulation")
        }
    }) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                property("pointer-events", if (visible) "auto" else "none")
            }
        }) { content() }
    }
}

@Composable
fun MobileDashboardHomeHeader(
    totalBadgeCount: Int,
    userLabel: String,
    activeBuildName: String,
    onUserClick: () -> Unit
) {
    Div({
        style {
            width(100.percent)
            height(30.percent)
            minHeight(128.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(8.px, 0.px)
            boxSizing("border-box")
            property("flex-shrink", "0")
        }
    }) {
        Div({
            style {
                width(50.percent)
                height(100.percent)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(5.px) } }) {
                Img(src = LogoSettings.mobileDashboardBackgroundLogo) {
                    style {
                        property("width", "clamp(138px, 30vw, 276px)")
                        maxWidth(42.vw)
                        property("max-height", "clamp(46px, 10vw, 92px)")
                        property("object-fit", "contain")
                        property("object-position", "center")
                    }
                }
                if (activeBuildName.isNotBlank()) {
                    Span({ style { color(Color("rgba(241,245,249,0.44)")); property("font-size", "clamp(9px, 1.35vw, 16px)"); property("letter-spacing", "0.04em") } }) {
                        Text(activeBuildName)
                    }
                }
            }
        }
        Div({
            onClick { onUserClick() }
            style {
                width(50.percent)
                height(100.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(12.px)
                cursor("pointer")
            }
        }) {
            Div({ style { property("width", "clamp(46px, 10vw, 92px)"); property("height", "clamp(46px, 10vw, 92px)"); position(Position.Relative); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); borderRadius(50.percent); property("background", "rgba(8,10,14,0.32)"); property("border", "1px solid rgba(211,37,57,0.32)"); property("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.08)"); property("flex-shrink", "0") } }) {
                DashboardCountBadge(totalBadgeCount)
                Img(src = "images/user.svg") {
                    style {
                        property("width", "clamp(22px, 4.8vw, 44px)")
                        property("height", "clamp(22px, 4.8vw, 44px)")
                        property("filter", "brightness(0) invert(1)")
                        opacity(0.92)
                    }
                }
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.FlexStart)
                    gap(2.px)
                }
            }) {
                Span({ style { color(Color("rgba(241,245,249,0.48)")); property("font-size", "clamp(10px, 1.6vw, 17px)"); property("letter-spacing", "0.06em"); property("text-transform", "uppercase") } }) { Text("Willkommen") }
                Span({ style { color(Color("#F1F5F9")); property("font-size", "clamp(17px, 3vw, 30px)"); fontWeight("650"); textAlign("left"); property("letter-spacing", "-0.02em") } }) { Text(userLabel) }
            }
        }
    }
}

@Composable
fun MobileDashboardIdentityBar(
    userLabel: String,
    activeBuildName: String,
    onUserClick: () -> Unit
) {
    Div({
        onClick { onUserClick() }
        style {
            width(100.percent)
            minHeight(52.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.FlexStart)
            gap(14.px)
            padding(1.px, 2.px, 7.px, 2.px)
            boxSizing("border-box")
            property("flex-shrink", "0")
            cursor("pointer")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.FlexStart)
                color(Color.white)
            }
        }) {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px) } }) {
                Span({ style { color(Color("#F1F5F9")); fontSize(20.px); fontWeight("650"); lineHeight("1.12"); property("letter-spacing", "-0.025em") } }) {
                    Text("Willkommen, $userLabel")
                }
                if (activeBuildName.isNotBlank()) {
                    Span({ style { color(Color("rgba(241,245,249,0.46)")); fontSize(11.px); property("letter-spacing", "0.04em") } }) {
                        Text(activeBuildName)
                    }
                }
            }
        }
    }
}
